import React, { useMemo, useState } from 'react';
import {
    TextField,
    Button,
    MenuItem,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
} from '@mui/material';
import DrawerShell from './DrawerShell';
import { FieldGroup } from '../PanelComerciales.styles';
import { panelTheme } from '../PanelComerciales.theme';
import {
    Commercial,
    TEMPORAL_INACTIVATION_REASONS,
} from '../PanelComerciales.types';
import { isFutureOrTodayDate } from '../utils/validators';
import { PERMANENT_BLOCK_UNTIL } from '../utils/blockSentinel';
import { SellerConfigPatch } from 'src/redux/panel_comerciales/panel_comerciales.types';

interface BlockCommercialDrawerProps {
    open: boolean;
    onClose: () => void;
    commercials: Commercial[];
    period: string;
    onConfirm: (sellerId: number, patch: SellerConfigPatch, label: string) => void;
}

type Mode = 'temporal' | 'definitivo';

const todayIso = () => new Date().toISOString().slice(0, 10);

const BlockCommercialDrawer: React.FC<BlockCommercialDrawerProps> = ({
    open,
    onClose,
    commercials,
    period,
    onConfirm,
}) => {
    const [sellerId, setSellerId] = useState<number | ''>('');
    const [mode, setMode] = useState<Mode>('temporal');
    const [reason, setReason] = useState<string>('vacaciones');
    const [since, setSince] = useState<string>(todayIso());
    const [until, setUntil] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const sellers = useMemo(() => commercials.filter((c) => c.active), [commercials]);

    const close = () => {
        setSellerId('');
        setMode('temporal');
        setReason('vacaciones');
        setSince(todayIso());
        setUntil('');
        setNotes('');
        setError(null);
        onClose();
    };

    const submit = () => {
        if (!sellerId) return setError('Selecciona un comercial');
        if (!isFutureOrTodayDate(since)) return setError('Fecha de inicio inválida');
        if (mode === 'temporal' && !isFutureOrTodayDate(until)) {
            return setError('Fecha de fin inválida');
        }

        const seller = commercials.find((c) => c.id === sellerId);
        if (!seller) return setError('Comercial no encontrado');

        // Para "Definitivo" mandamos el centinela `9999-12-31` en `state_until`
        // (el motor de scoring/snapshot lo reconoce como bloqueo sin fin y
        // apaga `active_flag`). El motivo lógico es 'inactivo'. Para "Temporal"
        // mandamos el reason elegido + ambas fechas tipeadas por el usuario.
        const isPermanent = mode === 'definitivo';
        const reasonForBackend = (isPermanent ? 'inactivo' : reason) as SellerConfigPatch['state'];
        const reasonLabel = isPermanent
            ? 'Bloqueo definitivo'
            : TEMPORAL_INACTIVATION_REASONS.find((r) => r.value === reason)?.label ?? reason;

        onConfirm(
            sellerId as number,
            {
                period,
                state: reasonForBackend,
                state_since: since,
                state_until: isPermanent ? PERMANENT_BLOCK_UNTIL : until,
                state_reason: reasonLabel,
                notes: notes || undefined,
            },
            isPermanent
                ? `${seller.name}: bloqueo definitivo desde ${since}`
                : `${seller.name}: ${reasonLabel} ${since} → ${until}`,
        );
        close();
    };

    return (
        <DrawerShell
            open={open}
            title='Desactivar / Bloquear comercial'
            description='Desactivación temporal (con fechas) o bloqueo definitivo.'
            onClose={close}
        >
            <FieldGroup>
                <label>Comercial</label>
                <TextField
                    size='small'
                    select
                    value={sellerId}
                    onChange={(e) => setSellerId(Number(e.target.value))}
                >
                    {sellers.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.name} ({s.team})
                        </MenuItem>
                    ))}
                </TextField>
            </FieldGroup>
            <FieldGroup>
                <label>Tipo</label>
                <FormControl>
                    <RadioGroup
                        row
                        value={mode}
                        onChange={(e) => setMode(e.target.value as Mode)}
                    >
                        <FormControlLabel value='temporal' control={<Radio size='small' />} label='Temporal (con fecha de fin)' />
                        <FormControlLabel value='definitivo' control={<Radio size='small' />} label='Definitivo (sin fecha de fin)' />
                    </RadioGroup>
                </FormControl>
            </FieldGroup>
            {mode === 'temporal' && (
                <FieldGroup>
                    <label>Motivo</label>
                    <TextField
                        size='small'
                        select
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    >
                        {TEMPORAL_INACTIVATION_REASONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                                {r.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </FieldGroup>
            )}
            <FieldGroup>
                <label>Desde</label>
                <TextField
                    size='small'
                    type='date'
                    value={since}
                    onChange={(e) => setSince(e.target.value)}
                />
            </FieldGroup>
            {mode === 'temporal' && (
                <FieldGroup>
                    <label>Hasta</label>
                    <TextField
                        size='small'
                        type='date'
                        value={until}
                        onChange={(e) => setUntil(e.target.value)}
                    />
                </FieldGroup>
            )}
            <FieldGroup>
                <label>Notas (opcional)</label>
                <TextField
                    size='small'
                    multiline
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />
            </FieldGroup>
            {error ? <span style={{ color: panelTheme.colors.danger, fontSize: 12 }}>{error}</span> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button onClick={close} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    onClick={submit}
                    variant='contained'
                    sx={{
                        textTransform: 'none',
                        background: panelTheme.colors.danger,
                        '&:hover': { background: '#B91C1C' },
                    }}
                >
                    {mode === 'definitivo' ? 'Bloquear' : 'Desactivar'}
                </Button>
            </div>
        </DrawerShell>
    );
};

export default BlockCommercialDrawer;
